import { SpineView } from '@play-co/timestep-spine';

/**
 * Wrapper to play and await spine animations to complete
 */
export async function playSpineAnimation(
  spine: SpineView,
  args: { animation: string; track?: number; loop?: boolean; clear?: boolean },
) {
  const loop = args.loop ?? false;

  if (args.clear) {
    spine.state.clearTracks();
    spine.state.clearListeners();
  }

  spine.state.setAnimation(args.track ?? 0, args.animation, loop);

  // Do not await for the animation to finish if loop is true
  if (loop) return;

  await new Promise((resolve) => {
    const listener = {
      complete: () => {
        spine.state.removeListener(listener);
        resolve(true);
      },
    };
    spine.state.addListener(listener);
  });
}

/**
 * Wrapper to play and await spine animation sequence to complete
 */
export async function playSpineAnimationSequence(
  spine: SpineView,
  args: {
    sequence: string[];
    track?: number;
    loopLast?: boolean;
    clear?: boolean;
  },
) {
  const loopLast = args.loopLast ?? false;

  for (let index = 0; index < args.sequence.length; index++) {
    const animation = args.sequence[index];
    await playSpineAnimation(spine, {
      animation,
      track: args.track,
      loop: loopLast && index === args.sequence.length - 1,
      clear: index === 0 ? args.clear : false,
    });
  }
}

/**
 * Play a spine animation with a maximum duration timeout.
 * Clears tracks after completion or timeout.
 */
export async function playSpineAnimationWithTimeout(
  spine: SpineView,
  args: { animation: string; timeout: number },
) {
  const animationPromise = playSpineAnimation(spine, {
    animation: args.animation,
    clear: true,
  });
  let timer: ReturnType<typeof setTimeout>;
  const timeoutPromise = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, args.timeout);
  });
  await Promise.race([animationPromise, timeoutPromise]);
  clearTimeout(timer!);
  spine.state.clearTracks();
  spine.state.clearListeners();
}

/**
 * Helper for swapping spine attachments in a slot.
 * The slot must already have the attachment set up in the spine skeleton.
 */
export async function setSpineAttachment(
  spine: SpineView,
  args: {
    slot: string;
    attachment: string;
  },
) {
  const skeleton = spine.skeleton;

  const slot = skeleton.findSlot(args.slot);
  if (!slot) {
    console.error(`Slot "${args.slot}" not found in spine skeleton`);
    return;
  }

  const attachment = skeleton.getAttachment(slot.data.index, args.attachment);
  if (!attachment) {
    console.error(
      `Attachment "${args.attachment}" not found for slot "${args.slot}"`,
    );
    return;
  }

  slot.setAttachment(attachment);
}
